import {defineStore} from 'pinia';
import Activity from '@/models/Activity';
import {computed, ref} from 'vue';

const STORE_ID = 'activity-store' as const;

export const useActivityStore = defineStore(STORE_ID, () => {
  const activities = ref<Activity[]>([]);
  const isEveryDoneActivityVisible = ref<boolean>(false);
  const loading = ref<boolean>(false);

  const filteredActivities = computed<Activity[]>(
    () => activities.value?.filter(
      (activity: Activity) => isEveryDoneActivityVisible.value || !activity.isDone
    ) ?? []
  );

  const expiredActivities = computed<Activity[]>(() => {
    const minDate = new Date();
    minDate.setHours(minDate.getHours() - 3);
    return activities.value?.filter(activity => isExpiredActivity(activity, minDate)) ?? [];
  });

  const isExpiredActivity = (activity: Activity, minDate: Date): boolean => {
    if (activity.isDone) return false;

    if (activity.endDateObject) {
      return isBeforeMinDate(activity.endDateObject, minDate);
    }
    return !!activity.startDateObject && isBeforeMinDate(activity.startDateObject, minDate);
  };

  const isBeforeMinDate = (date: Date, minDate: Date): boolean => {
    return date.getTime() < minDate.getTime();
  };

  const haveExpiredActivities = computed<boolean>(() => {
    return expiredActivities.value.length > 0;
  });
  const setActivities = (activitiesToSet: Activity[]) => {
    activities.value = activitiesToSet;
  };

  const addActivity = (activity: Activity) => {
    activities.value?.push(activity);
  };
  const updateActivity = (activity: Activity) => {
    activities.value = activities.value?.map((a: Activity) => a.id === activity.id ? activity : a) ?? [];
  };

  const deleteActivity = (activity: Activity) => {
    activities.value = activities.value?.filter(a => a.id !== activity.id) ?? [];
  };

  const setActivityIsDone = (activity: Activity, isDone: boolean) => {
    const activityIndex: number = activities.value!.findIndex(a => a.id === activity.id);
    if (activityIndex !== -1) {
      activities.value![activityIndex].isDone = isDone;
    }
  };

  const startLoading = () => {
    loading.value = true;
  };

  const stopLoading = () => {
    loading.value = false;
  };

  const getActivityById = ( id: number ): Activity | undefined  => {
    return activities.value.find( activity => activity.id === id );
  }

  return {
    activities,
    loading,
    filteredActivities,
    expiredActivities,
    isEveryDoneActivityVisible,
    haveExpiredActivities,
    setActivities,
    addActivity,
    updateActivity,
    deleteActivity,
    setActivityIsDone,
    startLoading,
    stopLoading,
    getActivityById
  };
});
